import { useEffect, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { settingsService } from "../../services/settingsService";
import { brevoTemplateService } from "../../services/brevoTemplateService";

interface EmailPreviewProps {
  content?: string;
  templateId?: number | string;
  templateParams?: Record<string, any>;
  subject?: string;
  preheader?: string;
}

export function EmailPreview({
  content,
  templateId,
  templateParams = {},
  subject = "PM-Gate Academy",
  preheader = "",
}: EmailPreviewProps) {
  const [socialLinks, setSocialLinks] = useState<any[]>([]);
  const [templateHtml, setTemplateHtml] = useState("");
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [fullHtml, setFullHtml] = useState("");

  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const response = await settingsService.getAllSocialLinks();
        const links =
          (response as any).links ||
          (response as any).data ||
          (Array.isArray(response) ? response : []);

        if (Array.isArray(links)) {
          setSocialLinks(
            links
              .filter((l: any) => l.isActive)
              .sort((a: any, b: any) => a.displayOrder - b.displayOrder),
          );
        }
      } catch (error) {
        console.error("Failed to load social links for preview:", error);
      }
    };
    fetchSocialLinks();
  }, []);

  useEffect(() => {
    const loadTemplateHtml = async () => {
      if (templateId) {
        setIsLoadingTemplate(true);
        try {
          const response =
            await brevoTemplateService.getTemplateById(templateId);
          setTemplateHtml(response.data?.htmlContent || "");
        } catch (error) {
          console.error("Failed to load template HTML:", error);
          setTemplateHtml("");
        } finally {
          setIsLoadingTemplate(false);
        }
      } else {
        setTemplateHtml(content || "");
      }
    };
    loadTemplateHtml();
  }, [templateId, content]);

  const substituteVariables = (
    html: string,
    params: Record<string, any>,
  ): string => {
    if (!html) return html;

    let result = html;

    Object.entries(params).forEach(([key, value]) => {
      if (value) {
        const placeholder = new RegExp(`\\{\\{${key}\\}\\}`, "g");
        result = result.replace(placeholder, String(value));
      }
    });

    return result;
  };

  const getSocialIcon = (platform: string) => {
    const icons: Record<string, string> = {
      facebook: `${
        import.meta.env.VITE_FRONTEND_URL || "http://localhost:3000"
      }/assets/social-icons/facebook.svg`,
      twitter: `${
        import.meta.env.VITE_FRONTEND_URL || "http://localhost:3000"
      }/assets/social-icons/twitter.svg`,
      instagram: `${
        import.meta.env.VITE_FRONTEND_URL || "http://localhost:3000"
      }/assets/social-icons/instagram.svg`,
      linkedin: `${
        import.meta.env.VITE_FRONTEND_URL || "http://localhost:3000"
      }/assets/social-icons/linkedin.svg`,
      youtube: `${
        import.meta.env.VITE_FRONTEND_URL || "http://localhost:3000"
      }/assets/social-icons/youtube.svg`,
      tiktok: `${
        import.meta.env.VITE_FRONTEND_URL || "http://localhost:3000"
      }/assets/social-icons/tiktok.svg`,
      telegram: `${
        import.meta.env.VITE_FRONTEND_URL || "http://localhost:3000"
      }/assets/social-icons/telegram.svg`,
    };
    return icons[platform] || icons.facebook;
  };

  const processedHtml = substituteVariables(templateHtml, templateParams);

  useEffect(() => {
    const socialLinksHtml = socialLinks
      .map(
        (link: any) => `
        <a href="${link.url}" target="_blank" style="text-decoration: none; margin: 0 4px;">
          <div style="display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; background: linear-gradient(135deg, #e07828 0%, #102040 100%); border-radius: 50%;">
            <img src="${getSocialIcon(
              link.platform,
            )}" alt="${link.platform}" width="20" height="20" style="border: 0; display: block; filter: brightness(0) invert(1);">
          </div>
        </a>
      `,
      )
      .join("");

    const finalContent = processedHtml || "<p>No content to preview</p>";

    const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="utf-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>${subject}</title>
      <style>
        body { margin: 0; padding: 0; width: 100% !important; background-color: white; color: #102040; font-family: sans-serif; }
        .footer { text-align: center; padding: 40px 0; border-top: 1px solid #e2e8f0; margin-top: 40px; }
      </style>
    </head>
    <body style="margin: 0; padding: 0;">
      <div style="padding: 20px;">
        ${finalContent}
      </div>
      ${
        socialLinks.length > 0
          ? `
        <div class="footer">
          ${socialLinksHtml}
          <p style="margin-top: 20px; color: #989797; font-size: 11px;">© PM-Gate Academy. All rights reserved.</p>
        </div>
      `
          : ""
      }
    </body>
    </html>
    `;

    setFullHtml(html);
  }, [processedHtml, socialLinks, subject, preheader]);

  if (isLoadingTemplate) {
    return (
      <div className="w-full h-full min-h-[500px] flex flex-col items-center justify-center bg-gray-50 text-[#989797] gap-3">
        <Loader2 size={32} className="animate-spin text-[#e07828]" />
        <p className="text-sm font-medium">Loading email content...</p>
      </div>
    );
  }

  if (!fullHtml || fullHtml.includes("<p>No content to preview</p>")) {
    return (
      <div className="w-full h-full min-h-[500px] flex flex-col items-center justify-center bg-gray-50 text-[#989797] p-8 text-center gap-4">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
          <AlertCircle size={32} className="text-gray-300" />
        </div>
        <div>
          <p className="text-base font-semibold text-[#102040]">
            Unable to Display Content
          </p>
          <p className="text-xs max-w-[240px] mt-1">
            This campaign may have been sent using an external template or the
            content is currently unavailable.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
      <iframe
        title="Email Preview"
        srcDoc={fullHtml}
        className="w-full h-full"
        style={{ minHeight: "600px" }}
      />
    </div>
  );
}
